import { ArrowLeft } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const ProteinFiberRatioDetail = () => {
  const navigate = useNavigate();

  const dailyData = Array.from({ length: 7 }, (_, i) => ({
    meal: `Meal ${i + 1}`,
    ratio: +(Math.random() * 3 + 2).toFixed(1),
  }));

  const weeklyData = [
    { day: "Mon", ratio: 3.2 },
    { day: "Tue", ratio: 2.8 },
    { day: "Wed", ratio: 3.5 },
    { day: "Thu", ratio: 4.1 },
    { day: "Fri", ratio: 3.0 },
    { day: "Sat", ratio: 3.8 },
    { day: "Sun", ratio: 2.9 },
  ];

  const monthlyData = [
    { week: "Week 1", ratio: 3.1 },
    { week: "Week 2", ratio: 3.4 },
    { week: "Week 3", ratio: 2.9 },
    { week: "Week 4", ratio: 3.2 },
  ];

  const currentRatio = 3.2;

  const getStatus = (ratio: number) => {
    if (ratio <= 2.5) {
      return { label: "Excellent", color: "text-success" };
    }
    if (ratio <= 4) {
      return { label: "Good", color: "text-primary" };
    }
    if (ratio <= 6) {
      return { label: "Moderate", color: "text-warning" };
    }
    return { label: "Poor", color: "text-destructive" };
  };

  const status = getStatus(currentRatio);

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/home")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Protein-Fiber Ratio
          </h1>
          <div className="w-10" />
        </div>

        {/* Today's Stats */}
        <Card className="from-chart-3/10 to-chart-3/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-sm text-muted-foreground">
              Today's Protein-to-Fiber Ratio
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">
              {currentRatio}
            </div>
            <p className={`text-sm font-semibold ${status.color}`}>
              {status.label}
            </p>
            <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="bg-chart-3 h-full rounded-full"
                style={{ width: `${Math.min((currentRatio / 8) * 100, 100)}%` }}
              />
            </div>
          </div>
        </Card>

        {/* What is Protein-Fiber Ratio */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Protein-Fiber Ratio?
          </h3>
          <p className="text-sm leading-relaxed text-muted-foreground">
            The Protein-to-Fiber Ratio measures the balance between protein
            intake and dietary fiber. A balanced ratio supports muscle recovery
            while maintaining gut health and digestive regularity. Too much
            protein relative to fiber can lead to digestive issues, while
            adequate fiber helps with nutrient absorption and sustained energy.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Excellent</span>
              <span className="text-success font-semibold">≤ 2.5</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Good</span>
              <span className="font-semibold text-primary">≤ 4.0</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Moderate</span>
              <span className="text-warning font-semibold">≤ 6.0</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Poor</span>
              <span className="font-semibold text-destructive">&gt; 6.0</span>
            </div>
          </div>
        </Card>

        {/* Charts */}
        <Card className="rounded-2xl border-0 shadow-sm">
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="m-4 grid w-full grid-cols-3 p-1">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="daily" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={dailyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="meal"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={10}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="ratio"
                    fill="hsl(var(--chart-3))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="weekly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={weeklyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="day"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="ratio"
                    stroke="hsl(var(--chart-3))"
                    strokeWidth={2}
                    dot={{ fill: "hsl(var(--chart-3))" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </TabsContent>

            <TabsContent value="monthly" className="px-4 pb-4">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthlyData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="week"
                    stroke="hsl(var(--muted-foreground))"
                    fontSize={12}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar
                    dataKey="ratio"
                    fill="hsl(var(--chart-3))"
                    radius={[8, 8, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Tips */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            Ways to Improve Your Ratio
          </h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-chart-3 mt-0.5">•</span>
              <span>
                Pair protein sources with high-fiber vegetables at every meal
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-3 mt-0.5">•</span>
              <span>
                Add beans, lentils, or chickpeas as protein-fiber combo foods
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-3 mt-0.5">•</span>
              <span>
                Include chia seeds, flaxseeds, or hemp seeds in smoothies
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-chart-3 mt-0.5">•</span>
              <span>
                Choose whole grains over refined options to boost fiber intake
              </span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default ProteinFiberRatioDetail;
